/**
 * /admin/ops/traces — SCRUM-318/326.
 * Trazas agrupadas por request_id (no una lista plana de spans), con
 * filtros de período/estado/duración, KPIs reales y drill-down a spans.
 * Recibe requestId por query param — cierra el link ya existente desde
 * Errors ("Ver traza") que hasta ahora apuntaba a una página que lo ignoraba.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { OpsSubnav } from "@/components/OpsSubnav";
import { OpsTenantBar, opsAuthHeaders, useOpsTenantId } from "@/components/admin/OpsTenantBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { OpsSpanRow, TraceSummaryRow } from "@/lib/ops-types";

export const Route = createFileRoute("/admin/ops/traces")({
  component: OpsTracesPage,
  head: () => ({ meta: [{ title: "Trazas — Operaciones" }] }),
  validateSearch: (s: Record<string, unknown>) => ({
    requestId: typeof s.requestId === "string" && s.requestId ? s.requestId : undefined,
  }),
});

function isoDaysAgo(days: number) {
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx]!;
}

function OpsTracesPage() {
  const { requestId: requestIdFromUrl } = Route.useSearch();
  const [tenantId, setTenantId] = useOpsTenantId();
  const [requestId, setRequestId] = useState(requestIdFromUrl ?? "");
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(1).slice(0, 16));
  const [statusFilter, setStatusFilter] = useState<"all" | "ok" | "error">("all");
  const [slowOnly, setSlowOnly] = useState(false);
  const [traces, setTraces] = useState<TraceSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailSpans, setDetailSpans] = useState<OpsSpanRow[] | null>(null);
  const [detailRequestId, setDetailRequestId] = useState<string | null>(null);

  const loadTraces = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ dateFrom: new Date(dateFrom).toISOString() });
      const res = await fetch(`/api/ops/traces?${params}`, {
        credentials: "include",
        headers: opsAuthHeaders(tenantId),
      });
      if (!res.ok) {
        setTraces([]);
        return;
      }
      const data = (await res.json()) as { traces?: TraceSummaryRow[] };
      setTraces(data.traces ?? []);
    } finally {
      setLoading(false);
    }
  }, [tenantId, dateFrom]);

  const loadSpansFor = useCallback(
    async (rid: string) => {
      const params = new URLSearchParams({ request_id: rid });
      const res = await fetch(`/api/ops/traces?${params}`, {
        credentials: "include",
        headers: opsAuthHeaders(tenantId),
      });
      if (!res.ok) {
        setDetailSpans([]);
        return;
      }
      const data = (await res.json()) as { spans?: OpsSpanRow[] };
      setDetailSpans(data.spans ?? []);
      setDetailRequestId(rid);
    },
    [tenantId],
  );

  useEffect(() => {
    void loadTraces();
  }, [loadTraces]);

  // Si llegamos desde "Ver traza" en Errors, abrir el drill-down directo.
  useEffect(() => {
    if (requestIdFromUrl) void loadSpansFor(requestIdFromUrl);
  }, [requestIdFromUrl, loadSpansFor]);

  const filtered = useMemo(() => {
    const q = requestId.trim().toLowerCase();
    return traces.filter((t) => {
      if (q && !t.requestId.toLowerCase().includes(q) && !t.firstSpanName.toLowerCase().includes(q)) return false;
      if (statusFilter === "ok" && !t.ok) return false;
      if (statusFilter === "error" && t.ok) return false;
      if (slowOnly && t.maxDurationMs < 500) return false;
      return true;
    });
  }, [traces, requestId, statusFilter, slowOnly]);

  const kpis = useMemo(() => {
    const durations = filtered.map((t) => t.totalDurationMs);
    const errors = filtered.filter((t) => !t.ok).length;
    return {
      total: filtered.length,
      errors,
      errorRate: filtered.length ? Math.round((errors / filtered.length) * 100) : 0,
      avgMs: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0,
      p95Ms: percentile(durations, 95),
    };
  }, [filtered]);

  return (
    <div>
      <h1 className="px-6 pt-5 pb-2 text-lg font-bold text-foreground">Trazas</h1>
      <div className="p-4 sm:p-6">
        <OpsSubnav hint="Trazas agrupadas por request_id (platform.span en Neon). Drill-down: Errors → Trace → Spans." />
        <OpsTenantBar tenantId={tenantId} onTenantChange={setTenantId} />

        <div className="glass-card mb-4 flex flex-wrap items-end gap-3 rounded-xl p-4">
          <div className="min-w-[220px] flex-1">
            <Label className="text-xs">Buscar (request_id / operación)</Label>
            <Input
              className="mt-1 bg-secondary/50 font-mono text-xs"
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
              placeholder="req_… o nombre de operación"
            />
          </div>
          <div>
            <Label className="text-xs">Desde</Label>
            <Input
              type="datetime-local"
              className="mt-1 bg-secondary/50 text-xs"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <select
            className="h-9 rounded-md border border-input bg-secondary/50 px-2 text-xs"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          >
            <option value="all">Todos los estados</option>
            <option value="ok">Solo OK</option>
            <option value="error">Solo error</option>
          </select>
          <label className="flex items-center gap-1.5 pb-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={slowOnly} onChange={(e) => setSlowOnly(e.target.checked)} />
            Solo lentas (&gt;500ms)
          </label>
          <Button type="button" size="sm" onClick={() => void loadTraces()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            Actualizar
          </Button>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {[
            { label: "Trazas", value: kpis.total },
            { label: "Errores", value: kpis.errors },
            { label: "Tasa de error", value: `${kpis.errorRate}%` },
            { label: "Duración prom.", value: `${kpis.avgMs}ms` },
            { label: "p95", value: `${kpis.p95Ms}ms` },
          ].map((k) => (
            <div key={k.label} className="glass-card rounded-lg p-3">
              <p className="text-[10px] text-muted-foreground">{k.label}</p>
              <p className="text-lg font-semibold tabular-nums">{k.value}</p>
            </div>
          ))}
        </div>

        <div className="glass-card overflow-x-auto rounded-xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>request_id</TableHead>
                <TableHead>Inicio</TableHead>
                <TableHead>Operación</TableHead>
                <TableHead>Spans</TableHead>
                <TableHead>Duración</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-xs text-muted-foreground">
                    {loading ? "Cargando…" : "Sin trazas en el período/filtro seleccionado."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.slice(0, 200).map((t) => (
                <TableRow key={t.requestId}>
                  <TableCell className="max-w-[160px] truncate font-mono text-xs">{t.requestId}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(t.startAt).toLocaleString("es-AR")}
                  </TableCell>
                  <TableCell className="text-xs">{t.firstSpanName || "—"}</TableCell>
                  <TableCell className="text-xs tabular-nums">{t.spanCount}</TableCell>
                  <TableCell className="text-xs tabular-nums">{t.totalDurationMs}ms</TableCell>
                  <TableCell className="text-xs">{t.ok ? "✓" : "✗"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => void loadSpansFor(t.requestId)}>
                      Ver trace
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={detailSpans != null} onOpenChange={(v) => !v && (setDetailSpans(null), setDetailRequestId(null))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">{detailRequestId}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Span</TableHead>
                  <TableHead>Inicio</TableHead>
                  <TableHead>ms</TableHead>
                  <TableHead>OK</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(detailSpans ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-6 text-center text-xs text-muted-foreground">
                      Sin spans para este request_id.
                    </TableCell>
                  </TableRow>
                )}
                {(detailSpans ?? []).map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.spanName}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(s.startAt).toLocaleString("es-AR")}
                    </TableCell>
                    <TableCell className="text-xs">{s.durationMs}</TableCell>
                    <TableCell className="text-xs">{s.ok ? "✓" : "✗"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {detailRequestId && (
            <Link
              to="/admin/ops/errors"
              search={{ q: detailRequestId } as never}
              className="text-xs text-primary hover:underline"
            >
              Ver errores relacionados con este request_id →
            </Link>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
